<template>
    <div>
        <v-dialog
            :value="isOpen"
            scrollable
            max-width="500px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        <template v-if="!isNewDock">
                            Edycja doku <strong>{{ dockData.name }}</strong>
                        </template>
                        <template v-else>
                            Nowy dok
                        </template>
                    </h1>
                </v-card-title>

                <v-card-text class="text-center">
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="dock.name"
                                label="Nazwa"
                                :error-messages="dockNameErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="dock.title"
                                label="Krótki tytuł"
                                :error-messages="dockTitleErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-select
                                v-model="dock.dockTypeId"
                                label="Typ"
                                :items="dockTypes"
                                item-value="id"
                                item-text="dockType"
                                no-data-text="Brak danych"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="dock.description"
                                label="Opis"
                                :error-messages="dockDescriptionErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="dock.storeIds"
                                :items="stores"
                                :search-input.sync="searchStoreId"
                                label="Magazyny:"
                                placeholder="Wybierz magazyny"
                                item-value="id"
                                item-text="name"
                                chips
                                clearable
                                multiple
                                outlined
                                dense
                                hide-details
                                @change="searchStoreId = null"
                            >
                                <template #selection="data">
                                    <v-chip
                                        close
                                        @update:active="removePropertyStore(data.item)"
                                    >
                                        {{ data.item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                    <v-checkbox
                        v-model="dock.isActive"
                        label="Czy aktywny?"
                        hide-details
                    />
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Anuluj"
                        :disabled="actionLoading"
                        @click="onClose"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        :loading="actionLoading"
                        @click="saveDock"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'DockDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        dockData: {
            type: Object,
            default: () => {}
        },
        dockTypes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            dock: {
                name: null,
                title: null,
                dockTypeId: null,
                description: null,
                isActive: true,
                storeIds: []
            },
            actionLoading: false,
        }
    },
    validations() {
        const validations = {
            dock: {
                name: {
                    required
                },
                dockTypeId: {
                    required
                },
                title: {
                    required
                },
                description: {
                    required
                },
            }
        }

        return validations
    },
    computed: {
        isNewDock() {
            return isEmpty(this.dockData)
        },

        stores() {
            return this.$store.state.shipping.configurations.configurations.stores
        },

        // Walidacje
        dockNameErrors() {
            const errors = []

            if (!this.$v.dock.name.$dirty) return errors
            !this.$v.dock.name.required && errors.push('Proszę podać nazwę.')

            return errors
        },
        dockTypeIdErrors() {
            const errors = []

            if (!this.$v.dock.dockTypeId.$dirty) return errors
            !this.$v.dock.dockTypeId.required && errors.push('Proszę wybrać typ.')

            return errors
        },
        dockTitleErrors() {
            const errors = []

            if (!this.$v.dock.title.$dirty) return errors
            !this.$v.dock.title.required && errors.push('Proszę podać krótki tytuł.')

            return errors
        },
        dockDescriptionErrors() {
            const errors = []

            if (!this.$v.dock.description.$dirty) return errors
            !this.$v.dock.description.required && errors.push('Proszę podać opis.')

            return errors
        },
    },
    watch: {
        dockData(value) {
            if (!isEmpty(value)) {
                this.dock = JSON.parse(JSON.stringify(value))
            }
        }
    },
    methods: {
        removePropertyStore(item) {
            this.dock.storeIds.splice(this.dock.storeIds.indexOf(item.id), 1)
        },
        saveDock() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            let action = null

            this.isNewDock ? action = 'createDock' : action = 'updateDock'

            this.actionLoading = true
            this.$store.dispatch(`shipping/notification/${action}`, this.dock)
                .then(() => {
                    this.$emit('on-confirm', action)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.onClose()
                })
        },
        onClose() {
            setTimeout(() => {
                this.dock.name = null
                this.dock.dockTypeId = null
                this.dock.title = null
                this.dock.description = null
                this.dock.storeIds = []
                this.dock.isActive = true
                this.$v.$reset()
            }, 500)

            this.$emit('on-close')
        },
    }
}
</script>